// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Role from "./role.js"
import Subject from "./subject.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "role_id": {
      "column": {
        "default": null,
        "name": "role_id",
        "null": false,
        "type": "uuid"
      },
      "name": "role_id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "SubjectRole",
  "collectionKey": "subject_roles",
  "collectionName": "subject_roles",
  "i18nKey": "subject_role",
  "camelizedLower": "subjectRole",
  "name": "SubjectRole",
  "nameDasherized": "subject-role",
  "pluralName": "subject_roles",
  "ransackable_associations": [
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "role",
      "macro": "belongs_to",
      "resource_name": "Role",
      "through": null
    },
    {
      "className": "Subject",
      "collectionName": "subjects",
      "foreignKey": "subject_id",
      "name": "subject",
      "macro": "belongs_to",
      "resource_name": "Subject",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_role_id",
      "column": {
        "default": null,
        "name": "old_role_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_subject_id",
      "column": {
        "default": null,
        "name": "old_subject_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "role_id",
      "column": {
        "default": null,
        "name": "role_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "subject_id",
      "column": {
        "default": null,
        "name": "subject_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Subject",
      "collectionName": "subjects",
      "foreignKey": "subject_id",
      "name": "subject",
      "macro": "belongs_to",
      "resource_name": "Subject",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "role",
      "macro": "belongs_to",
      "resource_name": "Role",
      "through": null
    }
  ],
  "paramKey": "subject_role",
  "primaryKey": "id"
}

/** Frontend model for SubjectRole. */
export default class SubjectRole extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  roleId() {
    return this.readAttributeUnderscore("role_id")
  }

  /** @returns {boolean} */
  hasRoleId() {
    const value = this.roleId()

    return this._isPresent(value)
  }

  /** @returns {import("./subject.js").default | null} */
  subject() {
    return this._readBelongsToReflection({modelClass: Subject, reflectionName: "subject"})
  }

  /** @returns {Promise<import("./subject.js").default | null>} */
  loadSubject() {
    if (!("subjectId" in this)) throw new Error("Foreign key method wasn't defined: subjectId")

    const id = this.subjectId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "subject",
        model: this,
        modelClass: Subject
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./subject.js").default | null>} */
  ensureSubjectLoaded() {
    return this.ensureAssociationLoaded("subject")
  }

  /** @returns {import("./role.js").default | null} */
  role() {
    return this._readBelongsToReflection({modelClass: Role, reflectionName: "role"})
  }

  /** @returns {Promise<import("./role.js").default | null>} */
  loadRole() {
    if (!("roleId" in this)) throw new Error("Foreign key method wasn't defined: roleId")

    const id = this.roleId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "role",
        model: this,
        modelClass: Role
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./role.js").default | null>} */
  ensureRoleLoaded() {
    return this.ensureAssociationLoaded("role")
  }
}
